import type { Workbook, Worksheet } from 'exceljs';
import type { RecurringRevenueReport } from '@vy/types';
import { extractMonthlyColumnKeys } from './recurring-revenue-month-keys';
import { filterMonthColumns } from '../components/RecurringRevenueReportTable/filter-month-columns';
import { expandRecurringRevenueRows } from '../components/RecurringRevenueReportTable/recurring-revenue-report-row-expander';
import { MONTHS_PER_YEAR, type MonthlyValueMode } from '../components/RecurringRevenueReportTable/recurring-revenue-view-mode';

type BuildRecurringRevenueExportInput = {
  filteredReport: RecurringRevenueReport;
  fromDate: Date | null;
  toDate: Date | null;
  viewMode: MonthlyValueMode;
};

const LINEAGE_HEADERS = ['Customer', 'Contract', 'Product'];
const CURRENT_MRR_ORIGINAL_CURRENCY_HEADER = 'Current MRR (Original Currency)';
const CURRENT_MRR_USD_HEADER = 'Current MRR $';
const TOTAL_ARR_USD_HEADER = 'Total ARR $';
const SUMMARY_HEADERS = [
  CURRENT_MRR_ORIGINAL_CURRENCY_HEADER,
  CURRENT_MRR_USD_HEADER,
  TOTAL_ARR_USD_HEADER,
];
const USD_NUM_FORMAT = '"$"#,##0.00;[Red]"-$"#,##0.00';
const PLAIN_NUM_FORMAT = '#,##0.00;[Red]-#,##0.00';

export function buildRecurringRevenueExport(
  workbook: Workbook,
  input: BuildRecurringRevenueExportInput,
): void {
  for (const worksheet of workbook.worksheets) {
    workbook.removeWorksheet(worksheet.id);
  }

  const allMonthKeys = extractMonthlyColumnKeys(input.filteredReport);
  const visibleMonthKeys = filterMonthColumns(allMonthKeys, input.fromDate, input.toDate);
  const factor = input.viewMode === 'arr' ? MONTHS_PER_YEAR : 1;

  buildSheet(workbook, input.filteredReport, visibleMonthKeys, factor);
}

// One flat row per product (matching the on-screen table via the shared
// `expandRecurringRevenueRows`): no contract-level aggregate row, so the
// Product cell is always populated and monthly columns never double-count.
function buildSheet(
  workbook: Workbook,
  filteredReport: RecurringRevenueReport,
  monthKeys: string[],
  factor: number,
): void {
  const worksheet = workbook.addWorksheet('ARR MRR');

  const headers = [...LINEAGE_HEADERS, ...SUMMARY_HEADERS, ...monthKeys];
  worksheet.addRow(headers);
  styleHeaderRow(worksheet, 1);

  worksheet.views = [
    { state: 'frozen', xSplit: LINEAGE_HEADERS.length + SUMMARY_HEADERS.length, ySplit: 1 },
  ];

  for (const row of expandRecurringRevenueRows(filteredReport)) {
    const customerName = row.customerName ?? '';
    worksheet.addRow([
      customerName,
      row.contractName || customerName,
      row.productName,
      row.currentMrrInOriginalCurrency,
      row.currentMrrUsd,
      row.totalArrUsd,
      ...monthKeys.map((monthKey) => readMonthlyValue(row, monthKey) * factor),
    ]);
  }

  const originalCurrencyMrrColumn = headers.indexOf(CURRENT_MRR_ORIGINAL_CURRENCY_HEADER) + 1;
  const currentMrrUsdColumn = headers.indexOf(CURRENT_MRR_USD_HEADER) + 1;
  setColumnFormat(worksheet, originalCurrencyMrrColumn, originalCurrencyMrrColumn, PLAIN_NUM_FORMAT);
  setColumnFormat(worksheet, currentMrrUsdColumn, headers.length, USD_NUM_FORMAT);
  autoSizeColumns(worksheet, headers);
}

// Product rows carry per-product numbers; the no-product fallback row keeps the
// raw monthly breakdown object, so read `totalMrr` off it.
function readMonthlyValue(row: Record<string, unknown>, monthKey: string): number {
  const value = row[monthKey];
  if (typeof value === 'number') {
    return value;
  }
  if (value && typeof value === 'object' && 'totalMrr' in value) {
    return (value as { totalMrr: number }).totalMrr ?? 0;
  }
  return 0;
}

function styleHeaderRow(worksheet: Worksheet, rowNumber: number): void {
  const row = worksheet.getRow(rowNumber);
  row.font = { bold: true };
  row.alignment = { vertical: 'middle', horizontal: 'center' };
  row.commit();
}

function setColumnFormat(
  worksheet: Worksheet,
  startColumn: number,
  endColumn: number,
  format: string,
): void {
  for (let columnIndex = startColumn; columnIndex <= endColumn; columnIndex++) {
    const column = worksheet.getColumn(columnIndex);
    column.numFmt = format;
  }
}

function autoSizeColumns(worksheet: Worksheet, headers: string[]): void {
  for (const [index, header] of headers.entries()) {
    const column = worksheet.getColumn(index + 1);
    column.width = Math.max(14, header.length + 4);
  }
}
